"use client";

import {
  CHAIR_MATERIALS,
  formatChairMaterialLabel,
  type ChairMaterial,
} from "@/lib/listing/chairListingSpec";
import {
  DINING_CHAIR_COUNTS,
  DINING_TABLE_TOP_MATERIALS,
  formatDiningTableTopMaterialLabel,
  type DiningChairCount,
  type DiningTableChairSpecFormState,
  type DiningTableTopMaterial,
} from "@/lib/listing/diningTableChairListingSpec";
import { cn } from "@/lib/utils";

interface DiningTableChairSpecFieldsProps {
  value: DiningTableChairSpecFormState;
  onChange: (next: DiningTableChairSpecFormState) => void;
  className?: string;
}

function MaterialPills<T extends string | number>({
  options,
  selected,
  onSelect,
  formatLabel,
}: {
  options: readonly T[];
  selected: T | "";
  onSelect: (value: T) => void;
  formatLabel: (value: T) => string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={cn(
              "rounded-lg border px-3 py-2 text-sm font-medium transition",
              isSelected
                ? "border-primary bg-primary-50 text-primary-800"
                : "border-border bg-surface text-foreground hover:border-border-strong",
            )}
            aria-pressed={isSelected}
          >
            {formatLabel(option)}
          </button>
        );
      })}
    </div>
  );
}

export default function DiningTableChairSpecFields({
  value,
  onChange,
  className = "",
}: DiningTableChairSpecFieldsProps) {
  const setChairCount = (count: DiningChairCount) => {
    onChange({ ...value, diningChairCount: count });
  };

  const setTableTopMaterial = (material: DiningTableTopMaterial) => {
    onChange({ ...value, diningTableTopMaterial: material });
  };

  const setChairMaterial = (material: ChairMaterial) => {
    onChange({ ...value, chairMaterial: material });
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          How many chairs are included? <span className="text-red-500">*</span>
        </p>
        <MaterialPills
          options={DINING_CHAIR_COUNTS}
          selected={value.diningChairCount}
          onSelect={setChairCount}
          formatLabel={(count) => `${count} chairs`}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Table top material <span className="text-red-500">*</span>
        </p>
        <MaterialPills
          options={DINING_TABLE_TOP_MATERIALS}
          selected={value.diningTableTopMaterial}
          onSelect={setTableTopMaterial}
          formatLabel={formatDiningTableTopMaterialLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Chair material <span className="text-red-500">*</span>
        </p>
        <MaterialPills
          options={CHAIR_MATERIALS}
          selected={value.chairMaterial}
          onSelect={setChairMaterial}
          formatLabel={formatChairMaterialLabel}
        />
        <p className="text-xs text-text-light">
          Choose the main chair material so we can build your title.
        </p>
      </div>
    </div>
  );
}
